import { useCallback } from 'react';

import {
  Toaster,
  ToasterPlacements,
} from '@picsart/design-system/components/Toaster';
import {
  Snackbar,
  SnackbarSkins,
} from '@picsart/design-system/components/Snackbar';
import {
  Notification,
  NotificationStatuses,
  NotificationSizes,
} from '@picsart/design-system/components/Notification';
import {
  Button,
  ButtonSizeSM,
  ButtonVariants,
} from '@picsart/design-system/components/Button';
import { StackingContext } from '@picsart/design-system/foundation/stackingContext';

import { PrimaryColors } from '@picsart/design-system/foundation/primaryColors';
import { useAppStore, type IAppStore } from '@/store';
import useLocalNotification from '@/hooks/useLocalNotification';
import { ToastType } from '@/store/toast/types';

const selector = (store: IAppStore) => ({
  toast: store.toast,
  hideToast: store.hideToast,
});

const toastTypeToSnackbarSkin = (type: ToastType): SnackbarSkins => {
  switch (type) {
    case ToastType.Error:
      return SnackbarSkins.Error;
    case ToastType.Info:
      return SnackbarSkins.Information;
    case ToastType.Success:
    default:
      return SnackbarSkins.Success;
  }
};

const toastTypeToNotificationStatus = (
  type: ToastType
): NotificationStatuses => {
  switch (type) {
    case ToastType.Error:
      return NotificationStatuses.Error;
    case ToastType.Info:
      return NotificationStatuses.Information;
    case ToastType.Success:
    default:
      return NotificationStatuses.Success;
  }
};

/**
 * Single toast surface. A local notification (with an action button —
 * e.g. "Your creation is ready! [View]") takes precedence over the global
 * info/error toast. Local notifications use DS `Notification` (banner with
 * a textual action button); plain toasts use DS `Snackbar`. Both are hosted
 * in a DS `Toaster` positioned at the bottom-center.
 */
const ToastSurface = () => {
  const { toast: notification, hideToast: hideNotification } =
    useLocalNotification();
  const { toast: globalToast, hideToast: hideGlobalToast } =
    useAppStore(selector);

  const handleNotificationAction = useCallback(() => {
    notification?.action?.onClick();
    hideNotification();
  }, [notification, hideNotification]);

  const isOpen = Boolean(notification || globalToast);

  // Use the local notification's onClose if a notification is shown,
  // otherwise the global toast's hideToast.
  const handleClose = notification ? hideNotification : hideGlobalToast;

  return (
    <Toaster
      dataTestId="toast-surface"
      isOpen={isOpen}
      position={ToasterPlacements.BottomCenter}
      zIndex={StackingContext.ToastLayer}
      onClose={handleClose}
      withoutTiming
    >
      {notification ? (
        <Notification
          dataTestId="local-notification"
          status={toastTypeToNotificationStatus(notification.type)}
          size={NotificationSizes.Small}
          description={notification.message}
          onClose={hideNotification}
          actions={
            notification.action
              ? [
                  <Button
                    key="action"
                    dataTestId="notification-action"
                    skin={PrimaryColors}
                    variant={ButtonVariants.Text}
                    size={ButtonSizeSM}
                    onClick={handleNotificationAction}
                  >
                    {notification.action.label}
                  </Button>,
                ]
              : undefined
          }
        />
      ) : globalToast ? (
        <Snackbar
          dataTestId="toast"
          skin={toastTypeToSnackbarSkin(globalToast.type)}
          title={globalToast.message}
          handleClose={hideGlobalToast}
        />
      ) : null}
    </Toaster>
  );
};

export default ToastSurface;
